import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getOnboardingRedirectPath } from '../../utils/onboardingNavigation';
import { onboardingApi } from '../../api/onboardingApi';

interface OnboardingGuardProps {
  children: React.ReactNode;
}

const OnboardingGuard: React.FC<OnboardingGuardProps> = ({ children }) => {
  const location = useLocation();
  const [redirectPath, setRedirectPath] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  const isOnboardingInterview =
    location.pathname === '/interview' &&
    (location.state as { isOnboarding?: boolean } | null)?.isOnboarding === true;

  useEffect(() => {
    if (isOnboardingInterview) {
      setIsChecking(false);
      return;
    }

    let cancelled = false;

    const checkOnboarding = async () => {
      try {
        const profileResponse = await onboardingApi.getUserProfile();
        if (cancelled) return;

        const path = getOnboardingRedirectPath(profileResponse.data);
        setRedirectPath(path);
      } catch (error: unknown) {
        if (cancelled) return;

        const status = (error as { response?: { status?: number } })?.response?.status;
        setRedirectPath(status === 404 ? '/onboarding/preferences' : '/onboarding/preferences');
      } finally {
        if (!cancelled) {
          setIsChecking(false);
        }
      }
    };

    checkOnboarding();

    return () => {
      cancelled = true;
    };
  }, [isOnboardingInterview, location.pathname]);

  if (isOnboardingInterview) {
    return <>{children}</>;
  }

  if (isChecking) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '18px',
          color: '#666',
        }}
      >
        Loading...
      </div>
    );
  }

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
};

export default OnboardingGuard;
